import React, { useState, useEffect, useRef, useCallback } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GB, s_GC } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';
import { useStorage } from '../../../shared/hooks';
import { getGameSession, updateGameState } from '../../../shared/dbService';
import MultiplayerInviteModal from '../../../modals/MultiplayerInviteModal';
import MultiplayerResults from '../../../modals/MultiplayerResults';
import TurnBanner from './TurnBanner';

function ReactionGame(props) {
  const { lang, onBack, allUsers, friends, currentUserId, userName, invitePartner, difficulty = 1 } = props;
  // difficulty 1=easy (1.5-4s window), 2=medium (1-3s), 3=hard (0.5-2s)
  const MIN_DELAY = difficulty >= 3 ? 500  : difficulty >= 2 ? 1000 : 1500;
  const MAX_EXTRA = difficulty >= 3 ? 1500 : difficulty >= 2 ? 2000 : 2500;
  const nl = lang === 'nl';
  const [state, setState]   = useState('idle');
  const [ms, setMs]         = useState(null);
  const [scores, setScores] = useState([]);
  const [best, setBest]     = useStorage('gm_react_best', null);
  const [showInvite, setShowInvite]     = useState(false);
  const [partner, setPartner]           = useState(null);
  const [showResults, setShowResults]   = useState(null);

  // ── Real-friend sync ─────────────────────────────────────────────
  const [myTurn, setMyTurn]             = useState(true);
  const [partnerScore, setPartnerScore] = useState(null);
  const [serverPhase, setServerPhase]   = useState('idle');
  const pollRef      = useRef(null);
  const startRef     = useRef(null);
  const timerRef     = useRef(null);
  const myScoreRef   = useRef(null);   // keep latest ms for async poll closure
  const serverPhaseRef = useRef('idle'); // always-fresh phase for tap handler

  const isRealFriend = !!(partner && !partner.isAI && partner._token);
  const token        = partner?._token || null;
  const amInviter    = partner?._isInviter || false;

  useEffect(() => {
    if (invitePartner) setPartner(invitePartner);
  }, [invitePartner]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify GamesScreen when a real partner with a token is active (for voice chat)
  useEffect(() => {
    if (props.onRealPartner) props.onRealPartner(partner && !partner.isAI && partner._token ? partner : null);
  }, [partner]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { clearTimeout(timerRef.current); clearInterval(pollRef.current); }, []);

  // ── Reset server state when partner is set ───────────────────────
  useEffect(() => {
    if (!isRealFriend || !token) return;
    if (amInviter) {
      updateGameState(token, { type: 'reaction', phase: 'idle', inviterScore: null, joinerScore: null });
      setMyTurn(true);
    } else {
      // Joiner waits for inviter to go first
      setMyTurn(false);
    }
  }, [isRealFriend, token, amInviter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Poll for partner's turn/score ────────────────────────────────
  const poll = useCallback(async () => {
    if (!token || !isRealFriend) return;
    const data = await getGameSession(token);
    if (!data?.gameState || data.gameState.type !== 'reaction') return;
    const gs = data.gameState;

    setServerPhase(gs.phase);
    serverPhaseRef.current = gs.phase;

    const theirScore = amInviter ? gs.joinerScore  : gs.inviterScore;
    const myScoreGs  = amInviter ? gs.inviterScore : gs.joinerScore;

    if (theirScore !== null && theirScore !== undefined) {
      setPartnerScore(theirScore);
    }

    // Determine if it's now my turn
    // Inviter goes first (phase=idle), joiner goes after (phase=inviterDone)
    const shouldBeMyTurn = amInviter
      ? gs.phase === 'idle'
      : gs.phase === 'inviterDone';

    setMyTurn(prev => {
      if (shouldBeMyTurn && !prev) setState('idle'); // reset to idle when my turn arrives
      return shouldBeMyTurn;
    });

    // Both done → show results
    if (gs.phase === 'done' && myScoreGs !== null && theirScore !== null && !showResults) {
      clearInterval(pollRef.current);
      setShowResults({ myScore: myScoreGs, partnerScore: theirScore });
    }
  }, [token, isRealFriend, amInviter, showResults]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isRealFriend) return;
    poll();
    pollRef.current = setInterval(poll, 1500);
    return () => clearInterval(pollRef.current);
  }, [poll, isRealFriend]);

  // ── Round logic ──────────────────────────────────────────────────
  const startRound = () => {
    setState('ready'); setMs(null);
    timerRef.current = setTimeout(() => {
      setState('go');
      startRef.current = Date.now();
    }, MIN_DELAY + Math.random() * MAX_EXTRA);
  };

  const tap = async () => {
    if (state === 'go') {
      clearTimeout(timerRef.current);
      const t = Date.now() - startRef.current;
      setMs(t); myScoreRef.current = t;
      setState('done');
      setScores(p => [t, ...p].slice(0, 5));
      if (!best || t < best) setBest(t);
      saveGameScore('reaction', Math.max(1, 1000 - t));

      if (isRealFriend && token) {
        // Push my score, advance phase (use ref for always-fresh phase)
        const curPhase  = serverPhaseRef.current;
        const nextPhase = amInviter ? 'inviterDone' : 'done';
        const alreadyPartnerDone = amInviter
          ? curPhase === 'joinerDone'   // joiner already went (shouldn't happen — inviter goes first)
          : curPhase === 'inviterDone'; // inviter already done, now joiner finishes

        const finalPhase = alreadyPartnerDone ? 'done' : nextPhase;
        const newState = {
          type: 'reaction',
          phase: finalPhase,
          inviterScore: amInviter ? t : (alreadyPartnerDone ? partnerScore : null),
          joinerScore:  amInviter ? (alreadyPartnerDone ? partnerScore : null) : t,
        };
        await updateGameState(token, newState);
        setServerPhase(finalPhase);
        setMyTurn(false);

        if (finalPhase === 'done' && partnerScore !== null) {
          clearInterval(pollRef.current);
          setShowResults({ myScore: t, partnerScore });
        }
      } else if (partner) {
        // AI partner: simulate
        const ps = Math.floor(Math.random() * 80) + 160;
        setShowResults({ myScore: t, partnerScore: ps });
      }
    } else if (state === 'ready') {
      clearTimeout(timerRef.current); setState('toosoon');
    }
  };

  const resetGame = () => {
    setState('idle'); setMs(null); setMyTurn(amInviter || !isRealFriend);
    setPartnerScore(null); setServerPhase('idle');
    if (isRealFriend && token && amInviter) {
      updateGameState(token, { type: 'reaction', phase: 'idle', inviterScore: null, joinerScore: null });
    }
  };

  const avg = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
  const bgC = state === 'go' ? '#2A9E8230' : state === 'ready' ? '#D4873A18' : state === 'toosoon' ? '#C0505018' : _T.bg4;
  const waitingForPartner = isRealFriend && !myTurn;

  // ── Results screen ───────────────────────────────────────────────
  if (showResults) {
    const ps = showResults.partnerScore;
    return (
      <MultiplayerResults lang={lang}
        myScore={showResults.myScore} myLabel="ms"
        partnerId={partner?.id} partnerScore={ps} partnerLabel="ms"
        aiComment={nl
          ? `Je reactietijd van ${showResults.myScore}ms is ${showResults.myScore < ps ? 'beter dan' : 'iets langzamer dan'} ${partner?.name || 'de tegenstander'}. Blijf oefenen!`
          : `Your reaction time of ${showResults.myScore}ms is ${showResults.myScore < ps ? 'better than' : 'a bit slower than'} ${partner?.name || 'your opponent'}. Keep training!`}
        onClose={() => { setShowResults(null); resetGame(); }}
        onRematch={() => { setShowResults(null); resetGame(); }}
      />
    );
  }

  return (
    <div>
      {showInvite && (
        <MultiplayerInviteModal lang={lang} title={nl ? 'Reactietijd' : 'Reaction Time'} icon="⚡"
          allUsers={allUsers} friends={friends}
          onInvite={(user) => { setPartner(user); setShowInvite(false); }}
          onClose={() => setShowInvite(false)} />
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13 }}>← {nl ? 'Terug' : 'Back'}</button>
        <button onClick={() => setShowInvite(true)} style={s_GB({ fontSize: 11, padding: '5px 12px', borderRadius: 10 })}>👥 {nl ? 'Samen' : 'Together'}</button>
      </div>

      {/* Turn banner for real-friend */}
      {isRealFriend && (
        <TurnBanner
          myName={userName}
          partnerName={partner?.name || ''}
          partner={partner}
          myTurn={myTurn}
          myScore={ms !== null ? `${ms}ms` : '–'}
          partnerScore={partnerScore !== null ? `${partnerScore}ms` : '–'}
          statusMsg={
            waitingForPartner && state !== 'done'
              ? (nl ? `Wachten op ${partner?.name}...` : `Waiting for ${partner?.name}...`)
              : state === 'done' && !showResults
              ? (nl ? `${partner?.name} speelt nog...` : `${partner?.name} is still playing...`)
              : ''
          }
          lang={lang}
        />
      )}

      {/* Best/avg for solo */}
      {!isRealFriend && (
        <div style={{ ...s_GC({ marginBottom: 10 }), display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, textAlign: 'center' }}>
          <div><div style={{ fontSize: 13, color: C.silver }}>BEST</div><div style={{ fontSize: 22, color: C.teal, fontWeight: 700 }}>{best ? `${best}ms` : '–'}</div></div>
          <div><div style={{ fontSize: 13, color: C.silver }}>AVG</div><div style={{ fontSize: 22, color: _T.accent, fontWeight: 700 }}>{avg ? `${avg}ms` : '–'}</div></div>
        </div>
      )}

      {/* Waiting screen when it's partner's turn */}
      {waitingForPartner && (
        <div style={{ ...s_GC({ padding: '36px 20px', textAlign: 'center', marginBottom: 10 }) }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>⏳</div>
          <div style={{ color: C.silver, fontSize: 14 }}>
            {state === 'done'
              ? (nl ? `${partner?.name} speelt nog...` : `${partner?.name} is still playing...`)
              : (nl ? `${partner?.name} is aan de beurt` : `${partner?.name}'s turn`)}
          </div>
          {partnerScore !== null && (
            <div style={{ color: C.teal, fontSize: 24, fontWeight: 700, marginTop: 12 }}>{partnerScore}ms</div>
          )}
        </div>
      )}

      {/* Active game area — my turn */}
      {myTurn && (
        <>
          <div onClick={tap} style={{ ...s_GC({ textAlign: 'center', padding: '40px 20px', cursor: 'pointer', marginBottom: 12, background: bgC, transition: 'background 0.3s' }) }}>
            {state === 'idle'    && <><div style={{ fontSize: 56, marginBottom: 8 }}>👁</div><div style={{ color: C.silver }}>{nl ? 'Tik om te starten' : 'Tap to start'}</div></>}
            {state === 'ready'   && <><div style={{ fontSize: 56, marginBottom: 8 }}>⏳</div><div style={{ color: C.orange, fontWeight: 700, fontSize: 16 }}>{nl ? 'Wacht...' : 'Wait...'}</div></>}
            {state === 'go'      && <><div style={{ fontSize: 56, marginBottom: 8 }} className="popIn">⚡</div><div style={{ color: C.teal, fontSize: 22, fontWeight: 700 }}>{nl ? 'TIK NU!' : 'TAP NOW!'}</div></>}
            {state === 'done'    && <><div style={{ fontSize: 44, color: _T.accent, fontWeight: 200, marginBottom: 4 }}>{ms}<span style={{ fontSize: 14, color: C.silver, marginLeft: 4 }}>ms</span></div>{best === ms && <div style={{ color: C.teal, fontSize: 13, marginBottom: 4 }}>🏆 Record!</div>}<div style={{ color: C.silver, fontSize: 12 }}>{isRealFriend ? (nl ? `Wachten op ${partner?.name}...` : `Waiting for ${partner?.name}...`) : (nl ? 'Tik opnieuw' : 'Tap again')}</div></>}
            {state === 'toosoon' && <><div style={{ fontSize: 48, marginBottom: 8 }}>❌</div><div style={{ color: C.red, fontWeight: 700 }}>{nl ? 'Te vroeg!' : 'Too soon!'}</div></>}
          </div>
          {(state === 'idle' || state === 'toosoon') && (
            <button style={s_PB({ width: '100%' })} onClick={startRound}>
              {isRealFriend ? (nl ? 'Mijn beurt starten' : 'Start my turn') : 'Start'}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default ReactionGame;
